import {GANTT_CHART_ITEM_HEIGHT} from '@hconnect/ganttchart/src/constants'
import {GanttChartRows} from '@hconnect/ganttchart/src/modules'
import {GanttChartItem, TGanttChartItem} from '@hconnect/ganttchart/src/modules/GanttChartItem'
import {GanttChartBaseRow} from '@hconnect/ganttchart/src/types/common'
import {useUpmPlant} from '@hconnect/upm'
import {TextMedium} from '@hm/fundamental'
import {Badge} from '@mui/material'
import {RefObject, useCallback, memo} from 'react'

import {HotspotModal, HotspotChartItem} from '../Hotspot'

import {InstallationChartTooltip} from './InstallationChartTooltip'
import {AddInstallationModal, UpdateInstallationModal} from './InstallationModal'

import {
  useRefraModal,
  useKilns,
  useRefraSettings
} from '@refra/modules/common/providers'
import {Shutdown} from '@refra/modules/common/types'
import {mapColorConfig} from '@refra/modules/refra-planning/helpers/mapColorConfig'
import {useEditMaterialInstallation} from '@refra/modules/refra-planning/hooks/mutations/useEditMaterialInstallation'
import {
  useKilnSettings,
  useKilnShutdownsRefractories
} from '@refra/modules/refra-planning/providers'
import {HotSpot, MaterialInstallation} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

const ITEM_WIDTH_BREAKPOINT = 2

const _InstallationChart = ({
  shutdown,
  stepWidth,
  kilnLength,
  materialInstallations,
  xAxis,
  ganttContainerRef,
  materialInstallationsWithComments,
  isReadOnly = false,
  hotSpots
}: {
  shutdown: Shutdown
  stepWidth: number
  kilnLength: number
  xAxis: number[]
  materialInstallations?: MaterialInstallation[]
  materialInstallationsWithComments: string[]
  ganttContainerRef: RefObject<HTMLDivElement>
  isReadOnly?: boolean
  hotSpots: HotSpot[]
}) => {
  const {kilnConfig} = useKilnSettings()
  const {settings} = useRefraSettings()

  const {selectedKiln} = useKilns()
  const {kilnShutdownsRefractories} = useKilnShutdownsRefractories()

  const {mutateAsync: editMaterialInstallation} = useEditMaterialInstallation()

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})

  const {openModal} = useRefraModal()

  const onItemDragAndDrop = useCallback(
    async (item: TGanttChartItem) => {
      try {
        await editMaterialInstallation({
          material: {
            toMeter: Number((item.end / stepWidth).toFixed(2)),
            fromMeter: Number((item.start / stepWidth).toFixed(2))
          },
          kilnUpmId: selectedKiln.upmId,
          shutdownId: shutdown.id,
          plantUpmId: upmPlant.upmId,
          installationId: item.id
        })
        return true
      } catch {
        return false
      }
    },
    [editMaterialInstallation, selectedKiln.upmId, shutdown.id, stepWidth, upmPlant.upmId]
  )

  const ganttChartRows = [
    {
      id: 1,
      name: ''
    }
  ]

  const colorConfig = mapColorConfig(kilnConfig, stepWidth, [1])

  const displayGanttChartItem = useCallback(
    (item: MaterialInstallation) => {
      const assignedRefractory = kilnShutdownsRefractories[item.refractoryId]
      const useLocalColors = settings.useLocalColors ?? false
      const effectiveColor = useLocalColors 
        ? (assignedRefractory.customization?.color ?? assignedRefractory.color)
        : assignedRefractory.color
      const displayBadge =
        materialInstallationsWithComments.includes(item.id.toString()) ||
        (item.documentsCount !== undefined && item.documentsCount > 0)
      const itemWidth = item.toMeter - item.fromMeter
      const badgeContent =
        itemWidth < ITEM_WIDTH_BREAKPOINT ? (
          <div
            className={'bg-red-500 inline-block w-2 h-2 rounded-full absolute right-0 bottom-0'}
          />
        ) : (
          <Badge
            color={'error'}
            badgeContent={1}
            sx={{
              position: 'absolute',
              right: '12px',
              top: '12px'
            }}
          />
        )

      return (
        <GanttChartItem
          key={item.id}
          item={{
            id: item.id.toString(),
            start: item.fromMeter * stepWidth,
            end: item.toMeter * stepWidth,
            label: (
              <>
                <div
                  className={`text-center overflow-hidden text-ellipsis cursor-pointer ${
                    displayBadge && itemWidth > ITEM_WIDTH_BREAKPOINT && 'ml-1 mr-6'
                  }`}
                >
                  <TextMedium>{assignedRefractory.name}</TextMedium>
                  <div
                    className=" absolute top-[29px] left-0 w-full h-[4px] rounded-[10px] "
                    style={{background: effectiveColor}}
                  />
                  {item.isMortarRequired && settings.displayMortar && (
                    <div className="bg-black absolute top-[38px] left-0 w-full h-[4px] rounded-[10px] " />
                  )}
                  {displayBadge && badgeContent}
                </div>
              </>
            ),
            height: GANTT_CHART_ITEM_HEIGHT,
            background: <div className="bg-white w-full h-full" />,
            tooltip: {
              tooltipContent: (
                <InstallationChartTooltip materialInstallation={item} shutdownId={shutdown.id} />
              )
            }
          }}
          chartStartBoundary={0}
          chartEndBoundary={kilnLength * stepWidth}
          startOfChart={0}
          endOfChart={kilnLength * stepWidth}
          stepWidth={stepWidth}
          isReadOnly={isReadOnly}
          mutationKey={'editMaterialInstallation'}
          onItemClick={() => {
            openModal(
              <UpdateInstallationModal
                min={0}
                max={kilnLength}
                materialInstallation={item}
                shutdownId={shutdown.id}
                interventionDate={shutdown.interventionDate}
                assignedRefractory={assignedRefractory}
                isReadOnly={isReadOnly}
              />
            )
          }}
          dragAndDropItemCallback={onItemDragAndDrop}
        />
      )
    },
    [
      kilnLength,
      stepWidth,
      shutdown.id,
      shutdown.interventionDate,
      kilnShutdownsRefractories,
      isReadOnly,
      onItemDragAndDrop,
      openModal,
      materialInstallationsWithComments,
      settings.useLocalColors,
      settings.displayMortar,
      upmPlant.upmId
    ]
  )

  return (
    <div ref={ganttContainerRef} className="w-full h-[50px] overflow-hidden relative">
      <GanttChartRows<GanttChartBaseRow, number>
        chartStartBoundary={0}
        chartEndBoundary={kilnLength}
        stepWidth={stepWidth}
        isReadOnly={isReadOnly}
        xAxis={xAxis}
        colorConfig={colorConfig}
        onAddItem={(item) => {
          openModal(
            <AddInstallationModal
              fromMeter={item.selectedItemXAxisValue.toString()}
              toMeter={(item.selectedItemXAxisValue + 1).toString()}
              min={item.chartStartBoundary}
              max={item.chartEndBoundary}
              shutdownId={shutdown.id}
              interventionDate={shutdown.interventionDate}
            />
          )
        }}
        ganttChartRows={ganttChartRows}
      />

      {materialInstallations && materialInstallations.length > 0 && (
        <div className="absolute">
          <div className="w-full relative h-full">
            {materialInstallations.map((item) => displayGanttChartItem(item))}
          </div>
        </div>
      )}
      {hotSpots && hotSpots.length > 0 && settings.displayHotspots && (
        <div className="absolute">
          <div className="w-full relative h-full">
            {hotSpots.map((item) => (
              <HotspotChartItem
                shutdown={shutdown}
                hotspot={item}
                stepWidth={stepWidth}
                key={`${item.id}-${shutdown.id}`}
                onClick={() =>
                  openModal(<HotspotModal shutdown={shutdown} hotSpot={item} hotSpots={hotSpots} />)
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const InstallationChart = memo(_InstallationChart)
